import { queryCommonFlags } from "@/api/constants";
import { queryClient } from "@/queryClient";
import { useContext, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getFormFields,
  updateLeadTypeV2,
} from "@/modules/lead-page/api/lead-page.v2.api";
import {
  LeadType,
  UpdateLeadTypeRequestV2,
} from "@/modules/lead-page/dtos/index-v2";
import { getUsersByLeadTypeUId } from "@/modules/funnels/api/index.v2";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { toast } from "react-toastify";
import {
  AssignmentMethods,
  LeadTypeRadioAssignmentType,
} from "@/modules/lead-page/constants";
import useLeadTypesFilterOptions from "@/modules/common/hooks/lookups/lead-types-filter-options.hook";
import { LookupsOptions } from "@/modules/common/dtos/index.v2";
import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";
import { userHasPermission } from "@/modules/common/utils";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";

interface UseEditLeadTypeAndReferralProps {
  setModalOpen: (open: boolean) => void;
  selectedType: LookupsOptions | null;
  setSelectedType: (type: LeadType | null) => void;
}

const useEditLeadTypeAndReferral = ({
  setModalOpen,
  selectedType,
  setSelectedType,
}: UseEditLeadTypeAndReferralProps) => {
  const { lead } = useContext(LeadPageContext);

  const [radioValue, setRadioValue] = useState<LeadTypeRadioAssignmentType>(
    AssignmentMethods.AUTO_ASSIGN,
  );

  const { permissions: currentUserPermissions } =
    useContext(NawyPassportContext);

  const isAdminSales = userHasPermission(
    {
      name: PermissionsEnum.ADD_ALL_LEAD_TYPES,
      level: PermissionLevel.ALL,
    },
    currentUserPermissions,
  );

  const isContactCenter = userHasPermission(
    {
      name: PermissionsEnum.CONTACT_CENTER,
      level: PermissionLevel.WRITE,
    },
    currentUserPermissions,
  );

  const {
    leadTypesFilterOptions,
    leadTypesFilterOptionsLoading,
    setSearchTerm: setSearchTermLeadTypes,
    searchTerm: searchTermLeadTypes,
  } = useLeadTypesFilterOptions(
    {
      initialParams: {
        organizationUIds: lead?.data?.lead?.organization?.uid,
      },
    },
    !isAdminSales && !isContactCenter
      ? {
          includePoolTypes: false,
          excludePoolTypes: true,
          allowAllFunnels: true,
          includeOnlyDefaultPerCategory: true,
          includeOnlyAllowReferrals: true,
        }
      : {
          allowAllFunnels: true,
          includeOnlyDefaultPerCategory: true,
        },
  );

  const { data: formFields, isLoading: formFieldsLoading } = useQuery({
    queryKey: ["getFormFields", selectedType],
    queryFn: () => getFormFields(selectedType!.formUid!),
    enabled: !!selectedType?.formUid,
    ...queryCommonFlags,
  });

  const { data: usersByLeadType, isLoading: usersByLeadTypeLoading } = useQuery(
    {
      queryKey: ["getUsersByLeadType", selectedType],
      queryFn: () => getUsersByLeadTypeUId(selectedType!.uid!),
      enabled:
        !!selectedType?.uid && radioValue === AssignmentMethods.SELECT_USER,
      ...queryCommonFlags,
    },
  );

  const leadTypeMutation = useMutation({
    mutationFn: (data: UpdateLeadTypeRequestV2) =>
      updateLeadTypeV2(lead?.data.lead.uid!, data),
    onSuccess: (response: any) => {
      if (response?.data?.success) {
        toast.success(response?.data?.message || "Lead Type Updated", {
          position: "bottom-left",
          autoClose: 2000,
        });
      } else {
        toast.error(response?.data?.message || "Error Updating Lead Type", {
          position: "bottom-left",
          autoClose: 2000,
        });
      }

      queryClient.invalidateQueries({ queryKey: ["getLead"] });
      queryClient.invalidateQueries({ queryKey: ["getComments"] });
      setSelectedType(null);
      setModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Error Updating Lead Type",
        {
          position: "bottom-left",
          autoClose: 2000,
        },
      );
      setSelectedType(null);
      setModalOpen(false);
    },
  });

  return {
    leadTypes: {
      data: leadTypesFilterOptions,
      isLoading: leadTypesFilterOptionsLoading,
      searchTerm: searchTermLeadTypes,
      setSearchTerm: setSearchTermLeadTypes,
    },

    usersByLeadType: {
      data: usersByLeadType?.data,
      isLoading: usersByLeadTypeLoading,
    },

    leadTypeFormFields: {
      data: formFields?.data,
      isLoading: formFieldsLoading,
    },

    leadTypeMutation,
    setRadioValue,
    radioValue,
  };
};

export default useEditLeadTypeAndReferral;
